import { sanitizeSegment } from "../../storage/fsa.ts";
import { shortHash } from "../../storage/hash.ts";
import type { Notebook, OneNoteSection, OneNotePage } from "./types.ts";

/**
 * Output tree for OneNote (ARCHITECTURE.md §7):
 *
 *   onenote/<notebook-key>/<section-key>/<page-key>.html
 *   onenote/<notebook-key>/<section-key>/<page-key>_assets/<hash>.<ext>
 *
 * Every level gets a hash suffix the same way Teams' chatKey() does — none of
 * notebook, section or page titles are unique in OneNote (a user can have
 * several notebooks literally named "Untitled" or several pages named
 * "Notes"), so a suffix is load-bearing, not decorative.
 *
 * Nested section groups (a section group can itself contain sections or more
 * section groups) are not walked — only sections directly under a notebook.
 * Additive later, not a redesign, the same kind of disclosed gap as Mail's
 * unbuilt shared-mailbox support.
 */

const MAX_NAME_LENGTH = 60;

async function key(name: string | undefined, id: string, fallback: string): Promise<string> {
    const label = sanitizeSegment(name || fallback, MAX_NAME_LENGTH);
    const suffix = await shortHash(id, 8);
    return `${label || fallback}_${suffix}`;
}

export function notebookKey(notebook: Notebook): Promise<string> {
    return key(notebook.displayName, notebook.id, "notebook");
}

export function sectionKey(section: OneNoteSection): Promise<string> {
    return key(section.displayName, section.id, "section");
}

export function pageKey(page: OneNotePage): Promise<string> {
    return key(page.title, page.id, "page");
}

function sectionDir(notebook: string, section: string): string {
    return `onenote/${notebook}/${section}`;
}

export function pagePath(notebook: string, section: string, page: string): string {
    return `${sectionDir(notebook, section)}/${page}.html`;
}

export function pageAssetsDir(notebook: string, section: string, page: string): string {
    return `${sectionDir(notebook, section)}/${page}_assets`;
}

export function pageAssetDiskPath(
    notebook: string,
    section: string,
    page: string,
    hash: string,
    extension: string
): string {
    return `${pageAssetsDir(notebook, section, page)}/${hash}${extension}`;
}

/** Relative path from the page's own .html to one of its assets. */
export function pageAssetRelativePath(page: string, hash: string, extension: string): string {
    return `${page}_assets/${hash}${extension}`;
}
